import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import { combineReducers } from 'redux';
import {
    CreateCustomerRequest,
    CustomerDto,
    PricelistDto,
    PricelistPeriodDto,
    SaveCustomerDto
} from '../../../common/PricePrgClient';
import { createAsyncAction } from '../../../utils';

const namespaces = {
    customers: 'customers',
    selectedCustomerId: 'selectedCustomerId',
    customerUi: 'customerUi',
    pricelistMailing: 'pricelistMailing',
    customerPricelists: 'customerPricelists',
    pricelistPeriods: 'pricelistPeriods',
    purchasers: 'purchasers'
};

const customerEntity = new schema.Entity<CustomerDto>('customers', undefined, {
    idAttribute: 'customerId'
});

type UpdateCustomerRequest = SaveCustomerDto & { customerId: number };
type UpdateCustomerSuccess = UpdateCustomerRequest;

export const fetchCustomer = createAsyncAction<number, CustomerDto>(namespaces.customers, 'fetchCustomer');
export const fetchCustomers = createAsyncAction<void, CustomerDto[]>(namespaces.customers, 'fetchCustomers');
export const updateCustomer = createAsyncAction<UpdateCustomerRequest, UpdateCustomerSuccess>(
    namespaces.customers,
    'updateCustomer'
);

export const deleteCustomer = createAsyncAction<number, number>(namespaces.customers, 'deleteCustomer');

const customersSlice = createSlice({
    name: namespaces.customers,
    initialState: {} as { [customerId: string]: CustomerDto },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchCustomers.success, (state, action) => {
                const normalized = normalize(action.payload, [customerEntity]);
                return normalized.entities.customers ?? {};
            })
            .addCase(updateCustomer.success, (state, action) => {
                const { payload } = action;
                const customer = state[payload.customerId];
                if (customer != null) {
                    customer.customerNumber = payload.customerNumber;
                    customer.name = payload.name;
                    customer.city = payload.city ?? '';
                    customer.externalPricelistId = payload.externalPricelistId;
                    customer.responsible = payload.responsible;
                    customer.customerEmails = payload.customerEmails;
                    customer.pricelistMailAttachmentTypes = payload.pricelistMailAttachmentTypes;
                    customer.hasPricelistMailWeekdayRestrictions =
                        payload.hasPricelistMailWeekdayRestrictions;
                    customer.allowedPricelistMailWeekdays = payload.hasPricelistMailWeekdayRestrictions
                        ? payload.allowedPricelistMailWeekdays
                        : undefined;
                    customer.contactId = payload.contactId;
                    customer.language = payload.language ?? customer.erpLanguage;
                    customer.showValidToOnPricelistMail = payload.showValidToOnPricelistMail;
                    customer.pricelistMailLayoutType = payload.pricelistMailLayoutType;
                }
            })
            .addCase(deleteCustomer.success, (state, action) => {
                delete state[action.payload];
            })
            .addCase(fetchCustomer.success, (state, action) => {
                state[action.payload.customerId] = action.payload;
            })
});

const selectedCustomerId = createSlice({
    name: namespaces.selectedCustomerId,
    initialState: null as number | null,
    reducers: {
        customerSelected: (state, action: PayloadAction<number | null>) => action.payload
    }
});
export const { customerSelected } = selectedCustomerId.actions;

type SendPricelistToCustomerRequest = {
    customerId: number;
    pricelistId: number;
    validAt: string;
    mailSendoutId: string;
    purchaser?: string;
};

type SendPricelistToEmailRequest = {
    email: string;
    pricelistId: number;
    customerId: number;
    validAt: string;
    mailSendoutId: string;
    purchaser?: string;
};

export const sendPricelistToCustomer = createAsyncAction<SendPricelistToCustomerRequest, void, unknown>(
    namespaces.pricelistMailing,
    'sendPricelistToCustomer'
);

export const sendPricelistToEmail = createAsyncAction<SendPricelistToEmailRequest, void, unknown>(
    namespaces.pricelistMailing,
    'sendPricelistToEmail'
);

type SettingsUi = {
    isSendingPricelistMail: boolean;
    isSendPricelistToCustomerDialogOpen: boolean;
    isSendPricelistToEmailDialogOpen: boolean;
    isCreateCustomerDialogOpen: boolean;
    createCustomerParentCustomerId?: number;
};

const uiSlice = createSlice({
    name: namespaces.customerUi,
    initialState: {
        isSendingPricelistMail: false,
        isSendPricelistToCustomerDialogOpen: false,
        isSendPricelistToEmailDialogOpen: false,
        isCreateCustomerDialogOpen: false,
        createCustomerParentCustomerId: undefined
    } as SettingsUi,
    reducers: {
        showSendPricelistToCustomerDialog: (state, action: PayloadAction<boolean>) => {
            state.isSendPricelistToCustomerDialogOpen = action.payload;
        },
        showSendPricelistToEmailDialog: (state, action: PayloadAction<boolean>) => {
            state.isSendPricelistToEmailDialogOpen = action.payload;
        },
        showCreateCustomerDialog: (state, action: PayloadAction<boolean>) => {
            state.isCreateCustomerDialogOpen = action.payload;
            if (!action.payload) {
                state.createCustomerParentCustomerId = undefined;
            }
        },
        setParentCustomerId: (state, action: PayloadAction<number | undefined>) => {
            state.createCustomerParentCustomerId = action.payload;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(sendPricelistToCustomer.request, state => {
                state.isSendingPricelistMail = true;
            })
            .addCase(sendPricelistToCustomer.failure, state => {
                state.isSendingPricelistMail = false;
            })
            .addCase(sendPricelistToCustomer.success, state => {
                state.isSendingPricelistMail = false;
                state.isSendPricelistToCustomerDialogOpen = false;
            })
            .addCase(sendPricelistToEmail.request, state => {
                state.isSendingPricelistMail = true;
            })
            .addCase(sendPricelistToEmail.failure, state => {
                state.isSendingPricelistMail = false;
            })
            .addCase(sendPricelistToEmail.success, state => {
                state.isSendingPricelistMail = false;
                state.isSendPricelistToEmailDialogOpen = false;
            })
});

export const fetchPricelistsForCustomer = createAsyncAction<number, PricelistDto[]>(
    namespaces.customerPricelists,
    'fetchPricelistsForCustomer'
);

export const fetchPricelistPeriodsForPricelists = createAsyncAction<number[], PricelistPeriodDto[]>(
    namespaces.pricelistPeriods,
    'fetchPricelistPeriodsForPricelist'
);

const customerPricelistSlice = createSlice({
    name: namespaces.customerPricelists,
    initialState: [] as PricelistDto[],
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchPricelistsForCustomer.success, (_, action) => action.payload)
});

const customerPricelistPeriodSlice = createSlice({
    name: namespaces.pricelistPeriods,
    initialState: [] as PricelistPeriodDto[],
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchPricelistPeriodsForPricelists.success, (_, action) => action.payload)
});

export const {
    showSendPricelistToCustomerDialog,
    showSendPricelistToEmailDialog,
    showCreateCustomerDialog,
    setParentCustomerId
} = uiSlice.actions;

export const createCustomer = createAsyncAction<CreateCustomerRequest, CustomerDto>(
    namespaces.customers,
    'createCustomer'
);

export const fetchPurchasers = createAsyncAction<void, string[]>(namespaces.purchasers, 'fetchPurchasers');

const purchasersSlice = createSlice({
    name: namespaces.purchasers,
    initialState: [] as string[],
    reducers: {},
    extraReducers: builder => builder.addCase(fetchPurchasers.success, (_, action) => action.payload)
});

export default combineReducers({
    customers: customersSlice.reducer,
    selectedCustomerId: selectedCustomerId.reducer,
    ui: uiSlice.reducer,
    customerPricelists: customerPricelistSlice.reducer,
    customerPricelistPeriods: customerPricelistPeriodSlice.reducer,
    purchasers: purchasersSlice.reducer
});
